import type { PackagingSupplier } from '@/shared/hooks/api/packagingSuppliers'

export type PackagingSupplierFunnelStage =
  | 'NOT_OPENED'
  | 'OPENED'
  | 'ACKNOWLEDGED'
  | 'WITH_PACKAGING_ITEMS'
  | 'ALL_ITEMS_COMPLETE'

export type PackagingSuppliersStats = {
  totalSuppliers: number
  opened: number
  openedPercent: number
  acknowledged: number
  acknowledgedPercent: number
  withPackagingItems: number
  withPackagingItemsPercent: number
  allItemsComplete: number
  allItemsCompletePercent: number
}

function percent(numerator: number, denominator: number): number {
  if (denominator === 0) {
    return 0
  }
  return Math.round((numerator / denominator) * 100)
}

export function formatStatFraction(actual: number, total: number): string {
  return `${actual.toLocaleString()} / ${total.toLocaleString()}`
}

export function derivePackagingSupplierFunnelStage(
  supplier: Pick<
    PackagingSupplier,
    'onboardingStatus' | 'complianceProgress' | 'allPackedItemsComplete'
  >
): PackagingSupplierFunnelStage {
  const onboardingStatus = supplier.onboardingStatus ?? 'INVITE_SENT'
  const complianceProgress = supplier.complianceProgress ?? 'NO_ITEMS'

  if (supplier.allPackedItemsComplete === true) {
    return 'ALL_ITEMS_COMPLETE'
  }
  if (complianceProgress !== 'NO_ITEMS') {
    return 'WITH_PACKAGING_ITEMS'
  }
  if (onboardingStatus === 'ACKNOWLEDGED') {
    return 'ACKNOWLEDGED'
  }
  if (onboardingStatus !== 'INVITE_SENT') {
    return 'OPENED'
  }
  return 'NOT_OPENED'
}

export function derivePackagingSuppliersStats(
  suppliers: PackagingSupplier[]
): PackagingSuppliersStats {
  const totalSuppliers = suppliers.length
  let opened = 0
  let acknowledged = 0
  let withPackagingItems = 0
  let allItemsComplete = 0

  for (const supplier of suppliers) {
    const onboardingStatus = supplier.onboardingStatus ?? 'INVITE_SENT'
    const complianceProgress = supplier.complianceProgress ?? 'NO_ITEMS'

    if (onboardingStatus !== 'INVITE_SENT') {
      opened += 1
    }
    if (onboardingStatus === 'ACKNOWLEDGED') {
      acknowledged += 1
    }
    if (complianceProgress !== 'NO_ITEMS') {
      withPackagingItems += 1
    }
    if (supplier.allPackedItemsComplete === true) {
      allItemsComplete += 1
    }
  }

  return {
    totalSuppliers,
    opened,
    openedPercent: percent(opened, totalSuppliers),
    acknowledged,
    acknowledgedPercent: percent(acknowledged, totalSuppliers),
    withPackagingItems,
    withPackagingItemsPercent: percent(withPackagingItems, totalSuppliers),
    allItemsComplete,
    allItemsCompletePercent: percent(allItemsComplete, totalSuppliers),
  }
}
